import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { useAppSettings } from '../../hooks/useAppSettings';
import { useSubscription } from '../../hooks/useSubscription';
import AppCustomization from './AppCustomization';
import UserManagement from './UserManagement';
import PaymentManagement from './PaymentManagement';
import AnalyticsDashboard from './AnalyticsDashboard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiSettings, FiUsers, FiCreditCard, FiBarChart3, FiLogOut, FiShield } = FiIcons;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('customization');
  const { signOutAdmin } = useSuperAdmin();
  const { appSettings } = useAppSettings();
  const { subscriptionStats } = useSubscription();

  const tabs = [
    { id: 'customization', label: 'App Customization', icon: FiSettings },
    { id: 'users', label: 'User Management', icon: FiUsers },
    { id: 'payments', label: 'Payment Management', icon: FiCreditCard },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart3 }
  ];

  const handleSignOut = async () => {
    try {
      await signOutAdmin();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'customization':
        return <AppCustomization />;
      case 'users':
        return <UserManagement />;
      case 'payments':
        return <PaymentManagement />;
      case 'analytics':
        return <AnalyticsDashboard />;
      default:
        return <AppCustomization />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Super Admin Panel</h1>
                <p className="text-sm text-gray-600">{appSettings?.appName || 'MedTracker'} Administration</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{subscriptionStats?.totalUsers || 0}</div>
                  <div className="text-gray-500">Total Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{subscriptionStats?.paidUsers || 0}</div>
                  <div className="text-gray-500">Paid Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">AED {subscriptionStats?.totalRevenue || 0}</div>
                  <div className="text-gray-500">Revenue</div>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSignOut}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiLogOut} />
                <span>Sign Out</span>
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-purple-50 text-purple-600 border-r-4 border-purple-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <SafeIcon icon={tab.icon} className="text-xl" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderTabContent()}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;